import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toTestId } from '@blink/components/src/utils';

import { useHandleSupplyClick } from '../hooks/useHandleSupplyClick';

import { Store } from '@/shared/store';
import { formatSmart, formatWithMaxPrecision } from '@/shared/utils/helpers';
import { Agreement, ROLE } from '@/shared/api/borrow';
import { ConnectWalletWrapper } from '@/widgets/ConnectWalletWrapper';
import { AssetWithIcon, Button, ButtonSize, ButtonVariant, Progress, Skeleton } from '@/shared/ui';

type AssetsToLendRowsProps = {
    allItems: Agreement[] | any;
    itemIndex: number;
    role: ROLE;
};

export const AssetsToLendItemRow: FC<AssetsToLendRowsProps> = ({ itemIndex, allItems, role }) => {
    const navigate = useNavigate();

    const agreement = allItems[itemIndex];
    const {
        address,
        title,
        leverage,
        apy,
        totalDeposited,
        totalDepositThreshold,
        noWalletInfo,
        errorContract,
    } = agreement;

    const formattedApyRewards = Number(apy).toFixed(2);
    const currentApy = Number(formattedApyRewards);
    const totalSupply = formatWithMaxPrecision(totalDepositThreshold ?? '0', leverage?.decimals);
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);

    const handleSupplyClick = useHandleSupplyClick({
        leverage,
        address,
        currentApy,
        totalDeposited,
        totalDepositThreshold,
        agreement,
        role,
    });

    const supplyProgress = Math.round(
        (Number(totalDeposited) / Number(totalDepositThreshold)) * 100,
    );

    const handleDetails = () => {
        navigate(`/pool/${address}?role=lender`);
    };

    const supplyBalance = useMemo(() => {
        if (!leverage?.userBalance) {
            return 'N/A';
        }

        return formatSmart(
            formatWithMaxPrecision(leverage?.userBalance ?? '0', leverage?.decimals),
        );
    }, [leverage?.userBalance, leverage?.decimals]);

    return (
        <tr
            className='h-[72px] border-t border-gray-300 bg-white hover:bg-gray-100 [&_td]:font-semibold'
            data-testid={`supply-row-${toTestId(title)}`}
        >
            <td className='pl-6'>
                <span data-testid={`supply-title`}>{title}</span>
            </td>
            <td>
                <span data-testid={`supply-asset`}>
                    {leverage?.symbol ? (
                        <AssetWithIcon
                            assetName={leverage?.symbol}
                            className='[&_img]:h-[24px] [&_img]:w-[24px] [&_div]:text-[14px]'
                        />
                    ) : (
                        <Skeleton />
                    )}
                </span>
            </td>
            <td>
                <div className='mr-8'>
                    {totalSupply !== '0' && !noWalletInfo ? (
                        <>
                            <div data-testid={`supply-total`}>
                                <span data-testid={`supply-total-value`}>
                                    {formatSmart(totalSupply)}
                                </span>
                                <span data-testid={`supply-total-symbol`}>
                                    {' ' + (leverage?.symbol || '')}
                                </span>
                            </div>
                            <Progress
                                percent={supplyProgress}
                                strokeColor={Progress.colors.Secondary}
                            />
                        </>
                    ) : (
                        <span className='flex'>
                            <Skeleton className='mr-4' /> {leverage?.symbol || ''}
                        </span>
                    )}
                </div>
            </td>
            <td>
                <div>
                    <span data-testid={`supply-apy`}>{currentApy}</span>
                    <span>%</span>
                </div>
            </td>
            <td className='pr-4'>
                {supplyBalance !== 'N/A' && !noWalletInfo ? (
                    <div data-testid={`supply-balance`}>
                        {`${supplyBalance}`}{' '}
                        <span className='text-content-secondary font-normal'>
                            {leverage?.symbol}
                        </span>
                    </div>
                ) : (
                    <Skeleton />
                )}
            </td>
            <td>
                <div className='flex justify-end gap-5 mr-6'>
                    <ConnectWalletWrapper noWallet={noWalletInfo}>
                        <Button
                            variant={ButtonVariant.Primary}
                            size={ButtonSize.Medium}
                            className='w-24'
                            name={`supply-asset`}
                            onClick={handleSupplyClick}
                            disabled={!walletAddress || errorContract || !leverage}
                        >
                            Supply
                        </Button>
                    </ConnectWalletWrapper>

                    <Button
                        variant={ButtonVariant.Secondary}
                        size={ButtonSize.Medium}
                        className='w-24'
                        name={`supply-details`}
                        onClick={handleDetails}
                        disabled={noWalletInfo || errorContract || !leverage}
                    >
                        Details
                    </Button>
                </div>
            </td>
        </tr>
    );
};
